import { Box, Typography, useTheme } from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";

import { DragDropLayoutEditSchema } from "../../layouts/drag-and-drop-layout/types";
import PMBaseCard, { PMBaseCardProps } from "../pm-base-card/pm-base-card";

export interface PMSmallNumberProps extends Omit<PMBaseCardProps, "children"> {
  /** (`number | string`): The primary number to display in the center of the card. If a `number` is passed, it is formatted with US thousands separators (e.g. `1,234`). Pass a pre-formatted string for other locales. */
  number: number | string;
  /** (`string`): The title to display in the card header. */
  title: string;
  /** (`string`): Icon to display in the header. If a string is provided, it is resolved to a Material-UI icon. */
  icon?: string;
  /** (`number`): Number of decimal places to round the displayed number to. If omitted, the number is displayed with full precision (still with thousands separators). */
  roundToDecimals?: number;
}

/**
 * PMSmallNumber is a component that extends PMBaseCard to display a single, large number
 * centered in a card with optional icons in the header.
 *
 * Props:
 * - number: The primary number to display in the center of the card.
 * - icon: An optional icon to display in the card header, resolved if provided as a string.
 * - showInfoIcon: Determines if the info icon should be displayed in the header (default: false).
 * - showMenuIcon: Determines if the menu icon should be displayed in the header (default: false).
 * - title: The title to display in the card header.
 * - restCardProps: Additional props to be passed to the PMBaseCard component.
 */

export const PMSmallNumber: PimoReactComponent<PMSmallNumberProps> = ({
  number,
  icon = "Numbers",
  showInfoIcon = false,
  showMenuIcon = false,
  title,
  roundToDecimals,
  ...restCardProps
}) => {
  const theme = useTheme();

  return (
    <PMBaseCard
      icon={icon}
      title={title}
      showInfoIcon={showInfoIcon}
      showMenuIcon={showMenuIcon}
      {...restCardProps}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="h2"
          sx={{
            mt: 1,
            color: theme.palette.primary.contrastText,
          }}
        >
          {typeof number === "number"
            ? number.toLocaleString("en-US", {
                maximumFractionDigits: roundToDecimals ?? 20,
              })
            : number}
        </Typography>
      </Box>
    </PMBaseCard>
  );
};

export default PMSmallNumber;

export const PMSmallNumberCardEditSchema: DragDropLayoutEditSchema = {
  fields: [
    {
      property: "title",
      type: "string",
      label: "Card Title",
    },
    {
      property: "number",
      type: "number",
      label: "Display Number",
    },
    {
      property: "icon",
      type: "string",
      label: "Header Icon",
    },
    {
      property: "showInfoIcon",
      type: "boolean",
      label: "Show Info Icon",
    },
    {
      property: "showMenuIcon",
      type: "boolean",
      label: "Show Menu Icon",
    },
  ],
};
